import { t } from "i18next";
import { useContext, useEffect, useState } from "react";
import { getVideos, getVideoTags } from "../../../../services/video";
import { Video, VideoTag } from "../../../../interfaces/video";
import { Badge, Button, Col, Form, Row } from "react-bootstrap";
import { AdminVideoNew } from "./video-new";
import { AdminVideoDelete } from "./video-delete";
import { AdminVideoEdit } from "./video-edit";
import { UserContext } from "../../../../App";
import { VideoTagFilter } from "../../../general/video-tag-filter";

export function AdminVideoList() {
    const { userProfile } = useContext(UserContext);

    const is_administrator = (userProfile.role.filter((r: any) => r.id == 'padeltech_administrator').length > 0);
    const is_manager = (userProfile.role.filter((r: any) => r.id == 'padeltech_manager').length > 0);
    const is_coach = (userProfile.role.filter((r: any) => r.id == 'padeltech_coach').length > 0);

    const [videos, setVideos] = useState<Video[]>([]);
    const [showAddNew, setShowAddNew] = useState(false);
    const [showDelete, setShowDelete] = useState(false);
    const [showEdit, setShowEdit] = useState(false);
    const [selectedId, setSelectedId] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    const [tags, setTags] = useState<VideoTag[]>([]);
    const [searchText, setSearchText] = useState('');
    const [filterTagIds, setFilterTagIds] = useState<number[]>([]);

    useEffect(() => {
        getVideoTags()
            .then(response => setTags(response.data))
            .catch(error => console.log(error));

        getVideos('', 1, 12)
            .then(response => {
                const videosData = response.data;
                setVideos(videosData);
                setHasMore(videosData.length === 12);
            })
            .catch(error => {
                console.log(error);
            });
    }, []);

    const loadVideos = (search: string, tagIds: number[], page: number, append: boolean = false) => {
        getVideos(search, page, 12, tagIds.length > 0 ? tagIds : undefined)
            .then(response => {
                const videosData = response.data;
                if (append) {
                    setVideos(prev => [...prev, ...videosData]);
                } else {
                    setVideos(videosData);
                }
                setCurrentPage(page);
                setHasMore(videosData.length === 12);
            })
            .catch(error => {
                console.log(error);
            });
    };

    const handleSearchSubmit = (event: any) => {
        event.preventDefault();
        const s = event.target.search.value;
        setSearchText(s);
        loadVideos(s, filterTagIds, 1);
    }

    const handleTagFilterChange = (tagIds: number[]) => {
        setFilterTagIds(tagIds);
    }

    const handleTagClick = (tagId: number) => {
        const newTagIds = [tagId];
        setFilterTagIds(newTagIds);
        loadVideos(searchText, newTagIds, 1);
    }

    const handleLoadMore = (event: any) => {
        event.preventDefault();
        loadVideos(searchText, filterTagIds, currentPage + 1, true);
    }

    const handleAddNew = (event: any) => {
        event.preventDefault();
        setShowAddNew(true)
    }

    const refreshTags = () => {
        getVideoTags()
            .then(response => setTags(response.data))
            .catch(error => console.log(error));
    };

    const handleAddNewOnSave = (s: Video) => {
        const newVideos = [s, ...videos];
        setVideos(newVideos);
        setShowAddNew(false);
        refreshTags();
    }

    const handleAddNewOnClose = () => {
        setShowAddNew(false);
    }

    const handleDelete = (event: any) => {
        event.preventDefault();
        setSelectedId(event.currentTarget.dataset.id);
        setShowDelete(true);
    }

    const handleDeleteOnDelete = (id: number) => {
        setSelectedId(0);
        const newVideos = videos.filter((s: Video) => s.id != id);
        setVideos(newVideos);
        setShowDelete(false);
    }

    const handleDeleteOnClose = () => {
        setSelectedId(0);
        setShowDelete(false);
    }

    const handleEdit = (event: any) => {
        event.preventDefault();
        setSelectedId(event.currentTarget.dataset.id);
        setShowEdit(true);
    }

    const handleEditOnSave = (changedVideo: Video) => {
        setSelectedId(0);
        const newVideos = videos.map((v: Video) => {
            if (v.id == changedVideo.id) {
                return changedVideo;
            } else {
                return v;
            }
        });
        setVideos(newVideos);
        setShowEdit(false);
        refreshTags();
    }

    const handleEditOnClose = () => {
        setSelectedId(0);
        setShowEdit(false);
    }

    const handleTagCreated = (tag: VideoTag) => {
        setTags(prev => [...prev, tag]);
    };

    const handleTagUpdated = (tag: VideoTag) => {
        setTags(prev => prev.map(t => t.id === tag.id ? tag : t));
    };

    const handleTagDeleted = (tagId: number) => {
        setTags(prev => prev.filter(t => t.id !== tagId));
        setFilterTagIds(prev => prev.filter(id => id !== tagId));
    };

    return (
        <div className="video-list-container">
            <h1>
                { t('videos')}
            </h1>
            <div className="filters-container">
                <Form onSubmit={ handleSearchSubmit }>
                    <Form.Group className="field-search" controlId="search">
                        <Form.Control type="search" placeholder={ t("Write to search") } />
                    </Form.Group>
                    { tags.length > 0 &&
                        <div className="field-tag">
                            <VideoTagFilter
                                tags={tags}
                                selectedTagIds={filterTagIds}
                                onChange={handleTagFilterChange}
                            />
                        </div>
                    }
                    <div className="actions">
                        <Button variant="primary" type="submit">
                            { t("search") }
                        </Button>
                    </div>
                </Form>
            </div>
            <div className="grid-container">
                <Row>
                    { videos.map((v: Video) => {
                        return <Col key={ 'video-' + v.id } xs={12} md={6} lg={4} xl={3}>
                            <div className="video-card">
                                <div className="heading">
                                    <div className="media">
                                        { v.media.length > 0 &&
                                            <video controls>
                                                <source src={ v.media[0].url } />
                                            </video>
                                        }
                                    </div>
                                    <div className="meta">
                                        <div className="name">
                                            { v.name }
                                        </div>
                                        { v.tags && v.tags.length > 0 &&
                                            <div className="tags">
                                                { v.tags.map((tag: VideoTag) => (
                                                    <Badge key={tag.id} bg="secondary" className="me-1" role="button" onClick={() => handleTagClick(tag.id)}>{tag.name}</Badge>
                                                ))}
                                            </div>
                                        }
                                    </div>
                                </div>
                                <div className="actions">
                                    { (is_administrator || is_manager || (is_coach && v.created_by == userProfile.id)) &&
                                        <a href="#" className="icon-link edit" title={ t("Edit") } onClick={ handleEdit } data-id={ v.id }>
                                            <i className="bi bi-pencil-square"></i>
                                        </a>
                                    }
                                    { (is_administrator || is_manager || (is_coach && v.created_by == userProfile.id)) &&
                                        <a href="#" className="icon-link delete" title={ t("Edit") } onClick={ handleDelete } data-id={ v.id }>
                                            <i className="bi bi-trash"></i>
                                        </a>
                                    }
                                </div>
                            </div>
                        </Col>
                    })}
                </Row>
                { hasMore &&
                    <div className="load-more-container" style={{ textAlign: 'center', marginTop: '20px' }}>
                        <Button variant="primary" onClick={ handleLoadMore }>
                            { t("Load more") }
                        </Button>
                    </div>
                }
            </div>
            <a className="admin-add-new" href="#" onClick={ handleAddNew }>
                <i className="bi bi-plus-circle"></i>
            </a>
            { showAddNew &&
                <AdminVideoNew tags={tags} onSave={ handleAddNewOnSave } onClose={ handleAddNewOnClose } onTagCreated={handleTagCreated} onTagUpdated={handleTagUpdated} onTagDeleted={handleTagDeleted} />
            }
            { showDelete && (selectedId != 0) &&
                <AdminVideoDelete id={ selectedId } onDelete={ handleDeleteOnDelete } onClose={ handleDeleteOnClose } />
            }
            { showEdit && (selectedId != 0) &&
                <AdminVideoEdit id={ selectedId } tags={tags} onSave={ handleEditOnSave } onClose={ handleEditOnClose } onTagCreated={handleTagCreated} onTagUpdated={handleTagUpdated} onTagDeleted={handleTagDeleted} />
            }
        </div>

    )
}
